/**
 * Checkout Return Page (layoutless)
 *
 * Handles explicit redirects from the external checkout app.
 * The checkout app links here with query params indicating the outcome:
 *
 *   /checkout-return?order_id=X&status=success  → clear cart, fire purchase event, go to /thank-you
 *   /checkout-return?status=cancelled           → go to /cart (cart stays intact)
 *   /checkout-return (no params)                → go to /
 *
 * This is the "explicit" return path; the CheckoutReturnListener handles
 * the "implicit" path (bfcache / back-button navigation).
 */

'use client';

import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCartContext } from '@/lib/context/cart-context';
import { trackPurchase } from '@/lib/analytics';
import { deleteCookie, CLEAR_CART_COOKIE, CLEAR_CART_ORDER_COOKIE } from '@/lib/utils/cookies';

export function CheckoutReturnPage() {
  const { clearCart } = useCartContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    const status = searchParams.get('status');
    const orderId = searchParams.get('order_id');

    // Always clean up the checkout signal cookies regardless of path
    deleteCookie(CLEAR_CART_COOKIE);
    deleteCookie(CLEAR_CART_ORDER_COOKIE);

    if (status === 'success' && orderId) {
      clearCart();
      trackPurchase({ orderId });
      navigate(`/thank-you?orderId=${encodeURIComponent(orderId)}`, { replace: true });
      return;
    }

    if (status === 'success') {
      clearCart();
      navigate('/thank-you', { replace: true });
      return;
    }

    if (status === 'cancelled') {
      navigate('/cart', { replace: true });
      return;
    }

    navigate('/', { replace: true });
  }, [clearCart, navigate, searchParams]);

  return null;
}
